// The demo portal's 3-month trial, issued and verified by the central License
// Server (project license-server-62cfc, product `staff-portal`).
//
// SECURITY MODEL — identical to the CRM apps': the server signs every payload
// with an Ed25519 private key and this client verifies it with the embedded
// public key, both on the live response AND on the locally cached copy. The
// cache lives inside the visitor's own sandbox, which the Firestore rules let
// them write freely — that is safe precisely because a forged or hand-edited
// document cannot produce a valid signature. Every check fails CLOSED.
//
// WHAT IS DIFFERENT HERE, and why:
//
//   * instanceId is the visitor's AUTH UID, not the Firebase projectId (as in
//     crm-app) and not the sandbox namespace. Every visitor shares one project,
//     so the projectId would bind nothing; the namespace would let anyone
//     restart their three months with ?reset=1. The uid makes the trial
//     per person, which is what "3 months free" is meant to mean.
//
//   * There is no key to paste and no LicenseCard. The trial is minted
//     automatically on first sign-in by issueStaffPortalTrial, which
//     authenticates the visitor's own Firebase ID token instead of a shared
//     secret — the demo project is on Spark and has no functions of its own.
//
//   * There is no Free tier to fall back to. This product is all-or-nothing, so
//     an expired or unverifiable licence means the trial-ended screen rather
//     than a degraded portal.

import { doc, getDoc, setDoc } from "firebase/firestore";
import { DEMO_NS } from "./demoConfig";
import { resolveLicense, verifyLicenseSignature } from "./licenseVerify";

export { PRODUCT_ID, resolveLicense } from "./licenseVerify";

export const LICENSE_SERVER_URL =
  "https://us-central1-license-server-62cfc.cloudfunctions.net";

// Cached inside the visitor's own namespace, alongside everything else they own.
const licenseDocPath = () => `artifacts/${DEMO_NS}/public/data/settings/license`;

// Re-check with the server at most once a day; in between, the cached signature
// is enough.
const REVALIDATE_AFTER_MS = 24 * 3600000;

async function readCached(db) {
  try {
    const snap = await getDoc(doc(db, licenseDocPath()));
    return snap.exists() ? snap.data() : null;
  } catch {
    return null;
  }
}

async function writeCached(db, data) {
  await setDoc(
    doc(db, licenseDocPath()),
    { ...data, lastCheckedAtMs: Date.now() },
    { merge: true },
  ).catch(() => {});
}

// ── Server I/O ──────────────────────────────────────────────────────────────

// Every call carries the visitor's own Firebase ID token; the License Server
// verifies it against the demo project's certs, so a caller can only ever act
// on their own licence.
async function post(path, user, body) {
  const idToken = await user.getIdToken();
  return fetch(`${LICENSE_SERVER_URL}/${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify(body || {}),
  });
}

// Fetch (minting on first call) this visitor's licence.
//
// Three outcomes, NOT two — the same distinction the CRM clients enforce, for
// the same reason: a cold start or a 5xx is not a verdict, and must never end
// somebody's access.
//   reachable:false          → transport/HTTP failure. Keep whatever is cached.
//   valid:false + definitive → a real 200 verdict (revoked/expired). May lock.
//   valid:true               → verified, signed and bound to this visitor.
async function fetchLicense(user) {
  let res;
  try {
    res = await post("issueStaffPortalLicense", user);
  } catch {
    return { reachable: false };
  }
  if (!res.ok) return { reachable: false, error: `HTTP ${res.status}` };

  let payload;
  try {
    payload = await res.json();
  } catch {
    return { reachable: false, error: "bad_response" };
  }
  if (!payload.valid) {
    return { reachable: true, valid: false, definitive: true, payload, error: payload.error };
  }
  if (!(await verifyLicenseSignature(payload))) {
    return { reachable: true, valid: false, definitive: true, error: "signature_invalid" };
  }
  if ((payload.instanceId ?? "") !== user.uid) {
    return { reachable: true, valid: false, definitive: true, error: "instance_mismatch" };
  }
  return { reachable: true, valid: true, payload };
}

/**
 * The one entry point the boot sequence uses.
 *
 * Reads the cached licence, refreshes it from the server when it is missing or
 * a day old, and returns the resolved verdict. A round trip that fails for
 * transport reasons leaves the cache untouched and falls back to it, so a blip
 * in the License Server cannot lock a visitor out mid-term.
 */
export async function ensureLicense(db, user) {
  const cached = await readCached(db);
  const cachedVerdict = await resolveLicense(cached, user.uid);

  const age = cached?.lastCheckedAtMs ? Date.now() - cached.lastCheckedAtMs : Infinity;
  if (cachedVerdict.ok && age <= REVALIDATE_AFTER_MS) return cachedVerdict;

  const r = await fetchLicense(user);

  if (r.valid) {
    await writeCached(db, r.payload);
    return resolveLicense(r.payload, user.uid);
  }

  // A real verdict may lock the portal; a transport failure may not.
  if (r.valid === false && r.definitive) {
    await writeCached(db, {
      valid: false,
      signature: "",
      revokedReason: r.error || "invalid",
      plan: r.payload?.plan ?? cached?.plan ?? null,
      expiresAt: r.payload?.expiresAt ?? cached?.expiresAt ?? null,
    });
    return {
      ok: false,
      reason: r.error === "expired" ? "expired" : "invalid",
      plan: r.payload?.plan ?? cached?.plan ?? null,
      expiresAt: r.payload?.expiresAt ?? cached?.expiresAt ?? null,
    };
  }

  // Unreachable. Fall back to whatever the cache can still prove — a verified
  // signature inside the 30-day grace, or nothing.
  return cachedVerdict.ok
    ? cachedVerdict
    : { ok: false, reason: cached ? cachedVerdict.reason : "unreachable" };
}

// ── Upgrading to the 3-month trial ──────────────────────────────────────────

/**
 * Step 1 — submit the registration form. The server emails a 6-digit code to
 * the WORK address given here, which is deliberately not assumed to be the
 * Google address they signed in with.
 *
 * @returns {{sent: true, email} | {alreadyTrial: true}}
 * @throws  Error with a message fit to show the applicant
 */
export async function requestTrial(user, details) {
  let res;
  try {
    res = await post("requestStaffPortalTrial", user, details);
  } catch {
    throw new Error("We couldn't reach the licence server. Please try again in a moment.");
  }
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || "Something went wrong. Please try again.");
  return body;
}

/**
 * Step 2 — confirm the code. On success the licence is upgraded in place and
 * the fresh signed payload is cached, so the caller can simply reload.
 */
export async function confirmTrial(db, user, code) {
  let res;
  try {
    res = await post("confirmStaffPortalTrial", user, { code });
  } catch {
    throw new Error("We couldn't reach the licence server. Please try again in a moment.");
  }
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || "That code could not be confirmed.");

  // Verify before trusting it, exactly as the boot path does — a payload is
  // never believed just because it arrived over HTTPS.
  if (!(await verifyLicenseSignature(body)) || (body.instanceId ?? "") !== user.uid) {
    throw new Error("The licence could not be verified. Please try again.");
  }
  await writeCached(db, body);
  return resolveLicense(body, user.uid);
}

/** Cheap read for the in-portal badge; no network. */
export async function readLicenseStatus(db, uid) {
  return resolveLicense(await readCached(db), uid);
}
